import { CustomError, ErrorOptions } from '../lib/error';
import { logError } from '../lib/logger';

import { Toast, DEFAULT_DURATION, ToastProps, ToastProvider as ToastProviderOrigin } from '@release/ui/Toast';
import { createContext, PropsWithChildren, useCallback, useMemo, useState } from 'react';

type ShowToastOriginOption = Pick<ToastProps, 'variant' | 'message' | 'duration'> & { e?: Error };
type ShowToastOption = Pick<ToastProps, 'duration'>;
type ShowErrorToastOption = Pick<ToastProps, 'duration'> & ErrorOptions;
type ShowToastFn = (message: string, options?: ShowToastOption) => CloseToastFn;
type ShowErrorToastFn = (e: unknown, options?: ShowErrorToastOption) => CloseToastFn;
type ToastState = ToastProps & { id: string };

type CloseToastFn = () => void;
interface IToastContext {
  toast: {
    error: ShowErrorToastFn;
    info: ShowToastFn;
    success: ShowToastFn;
  };
}

export const ToastContext = createContext<IToastContext>({
  toast: {
    error: () => () => {},
    info: () => () => {},
    success: () => () => {},
  },
});

export const ToastProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const toastOrigin = useCallback((args: ShowToastOriginOption) => {
    switch (args.variant) {
      case 'error':
        logError(args.e);
        break;
    }
    const duration = args.duration || DEFAULT_DURATION;
    const id = crypto.randomUUID();
    const onClose = () => {
      setToasts((prev) => prev.map((toast) => (toast.id === id ? { ...toast, isOpen: false } : toast)));
      setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
      }, 300);
    };
    setToasts((prev) => [...prev, { ...args, isOpen: true, id, duration, onClose }]);
    return onClose;
  }, []);
  const toast = useMemo(
    () => ({
      error: (e: unknown, options?: ShowErrorToastOption) => {
        const customError = new CustomError(e, options);
        const message = customError.description();
        // target/action等のコンテキストを保持したままDatadogへ送るため、加工前のeではなくcustomErrorを渡す
        return toastOrigin({ variant: 'error', message, duration: 3 * 1000, ...options, e: customError });
      },
      info: (message: string, options?: ShowToastOption) => toastOrigin({ variant: 'info', message, ...options }),
      success: (message: string, options?: ShowToastOption) => toastOrigin({ variant: 'success', message, ...options }),
    }),
    [toastOrigin]
  );
  return (
    <ToastProviderOrigin>
      <ToastContext.Provider value={{ toast }}>
        {children}
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} />
        ))}
      </ToastContext.Provider>
    </ToastProviderOrigin>
  );
};
